import React, { useEffect, useState } from "react";
import { WizardChildProps } from "../../types/props";
import { ROUTES } from "../../constants/clientRoutes";
import i18n from "../../i18n/config";
import { useHistory } from "react-router-dom";
import { ACTION_DISPLAY_TYPES, ALLOCATION_TYPES } from "../../constants/enums";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../types/redux";
import { AllocationType } from "../../types/api/allocations";

import Action from "../../components/Common/Action";
import AllocationPercentageTable from "../../components/Allocations/AllocationPercentageTable";
import { SetAllocationsToSubmit } from "../../actions/allocations";

const PercentageSplit = (props: WizardChildProps) => {
  const { setTitle, setForwardButton } = props;
  const dispatch = useDispatch();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [wageAmount, setWageAmount] = useState<string>("");
  const [calculate, setCalculate] = useState(false);
  const [totalPercent, setTotalPercent] = useState(0);
  const [totalFees, setTotalFees] = useState<string>("");
  const selectedAllocations = useSelector((state: RootState) => state.allocations.selectedAllocations);
  const history = useHistory();
  const initialPercentValuesState: { [key: string]: number } = {};
  const initialForwardButtonState = {
    text: i18n.t("COMMON.NEXT"),
    onClick: () => handleNextClick(),
    disabled: true,
    loading: false
  };

  /**
   * Set initial percent state based on how many allocations have been selected
   */
  selectedAllocations.forEach((allocation: AllocationType) => {
    initialPercentValuesState[allocation.id] = 0;
  });

  const [percentValues, setPercentValues] = useState(initialPercentValuesState);

  /**
   * On initial render set up title and buttons
   */
  useEffect(() => {
    if (!selectedAllocations.length) {
      history.replace(ROUTES.ALLOCATIONS.INDEX);
      return;
    }

    setTitle(i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.TITLE"));
    setForwardButton(initialForwardButtonState);
  }, []);

  /**
   * When total percent changes (i.e. one of the rows percentage inputs was changed), then check if total is 100 or not to display error message to user
   */
  useEffect(() => {

    // Calculate percent total
    const total = Object.values(percentValues).reduce((acc: number, curr: number) => {
      return acc + curr;
    }, 0) as number;
    const values = Object.values(percentValues);

    setTotalPercent(total);
    calculateFees();

    // if percent total is 100, remove error and enable button to move to next page
    if (total !== 100) {
      handlePercentValueChange(true, i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.ERRORS.TOTAL_NOT_EQUAL_100"), true);
    } else if (values.some((value: number) => value < 5) && !values.some((value: number) => value === 100)) {
      handlePercentValueChange(true, i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.ERRORS.MUST_BE_OVER_5"), true);
    } else {
      handlePercentValueChange(false, "", false);
    }

    // if every value is 0, then user hasn't changed any percentages, don't show error
    if (values.every((value) => value === 0)) {
      setShowError(false);
    }

  }, [percentValues]);

  const calculateFees = () => {
    const fees = selectedAllocations.reduce((acc: number, curr: AllocationType) => {
      if (!curr.selected && percentValues[curr.id] > 0) {
        return acc + curr.wireFee;
      } else {
        return acc;
      }
    }, 0);

    const feesString = fees?.toLocaleString(navigator.language, { style: "currency", currency: selectedAllocations[0].productCurrency.code });

    setTotalFees(feesString || "");
  }

  const handleCalculateClick = () => {
    if (!showError) {
      setCalculate(!calculate);
    }
  }

  const handleNextClick = () => {
    const selectedAllocationsWithPercentages = selectedAllocations.map((allocation: AllocationType) => {
      return {
        ...allocation,
        selectedForFreeTransfer: allocation.selected,
        percentage: percentValues[allocation.id]
      }
    });

    dispatch(SetAllocationsToSubmit(selectedAllocationsWithPercentages));
    history.push(ROUTES.ALLOCATIONS.CONFIRMATION, { type: ALLOCATION_TYPES.PERCENTAGE });
  }

  /**
   * function to handle percent value change
   * @param hasError 
   * @param message 
   * @param disabled 
   */
  const handlePercentValueChange = (hasError: boolean, message: string, disabled: boolean) => {
    setShowError(hasError);
    setErrorMessage(message);
    setForwardButton({
      ...initialForwardButtonState,
      disabled
    })
  }

  if (!selectedAllocations.length) {
    return null;
  }

  return (
    <>
      <p>{i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.P1")}</p>
      <p>{i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.P2")}</p>
      <p>{i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.P3")}</p>

      <div className="calculate-wrapper spacing-bottom-medium spacing-top-medium">
        <label htmlFor="calculate">{i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.CALCULATE_LABEL")}</label>
        <input type="number" id="calculate" className="form-control hide-arrows" onChange={(e) => setWageAmount(e.target.value)} value={wageAmount} />
        <Action
          title={i18n.t("PAGES.ALLOCATIONS.PERCENTAGE_SPLIT.BUTTON_TEXT")}
          onClick={handleCalculateClick}
          displayType={ACTION_DISPLAY_TYPES.PRIMARY}
        />
      </div>

      <AllocationPercentageTable
        allocations={selectedAllocations}
        percentValues={percentValues}
        setPercentValues={setPercentValues}
        calculate={calculate}
        wageAmount={wageAmount}
        totalPercent={totalPercent}
        totalFees={totalFees}
      />

      {showError && <p className="error">{errorMessage}</p>}
    </>
  );
}

export default PercentageSplit;